import { Controller } from "@hotwired/stimulus"

// Rewrites a <time datetime="..."> from the SSR fallback (app zone) into the
// browser's local timezone. Leaves the fallback in place if Intl is missing
// or the datetime can't be parsed.
//
// `format` value:
//   datetime     — date + time + short zone (default)
//   date         — month + day + year
//   ordinal-day  — "September 12th" (local calendar day, no year or clock)
export default class extends Controller {
  static values = { format: { type: String, default: "datetime" } }

  connect() {
    const iso = this.element.getAttribute("datetime")
    if (!iso || typeof Intl === "undefined" || !Intl.DateTimeFormat) return

    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return

    const formatted = this.#format(date)
    if (formatted) this.element.textContent = formatted
  }

  #format(date) {
    switch (this.formatValue) {
      case "date":
        return new Intl.DateTimeFormat(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric"
        }).format(date)
      case "ordinal-day": {
        const parts = new Intl.DateTimeFormat(undefined, {
          month: "long",
          day: "numeric"
        }).formatToParts(date)
        const month = parts.find((part) => part.type === "month")?.value
        const day = Number(parts.find((part) => part.type === "day")?.value)
        if (!month || Number.isNaN(day)) return null
        return `${month} ${day}${this.#ordinalSuffix(day)}`
      }
      default:
        return new Intl.DateTimeFormat(undefined, {
          dateStyle: "long",
          timeStyle: "short",
          timeZoneName: "short"
        }).format(date)
    }
  }

  #ordinalSuffix(day) {
    const tens = day % 100
    if (tens >= 11 && tens <= 13) return "th"
    switch (day % 10) {
      case 1: return "st"
      case 2: return "nd"
      case 3: return "rd"
      default: return "th"
    }
  }
}
